/**
 * pages/Penjualan.js
 * Sales transactions (Phase T1, PRD §5.1). A day navigator frames the view — the table shows
 * one day's PENJUALAN + TRANSFER transactions at a time (by transaction date, not creation
 * date), with a search+selector scoped to that day. TRANSFER rows share this list per the
 * PRD ("no fourth page" — "its economic shape is a sale by the source branch") with a
 * distinct TRANSFER badge and a dual-bucket Stok cell; PEMBELIAN lives on its own page
 * (Pembelian.js, T1c) and never appears here.
 *
 * **Section F, batch 4 — "Sembunyikan transfer internal"**: a toolbar checkbox (default OFF —
 * internal shown), mirroring KontakPage's own "Sembunyikan yang nonaktif". Spliced into the
 * filter chain BEFORE `dayTxns` (see `internalScoped`), so the day cards below are scoped by it
 * too, not just the rendered table — this is a deliberate choice: the three day cards state an
 * accrual total for "the day," and hiding internal transfers means the user wants that total to
 * mean "real business only," not a number the table no longer agrees with. See the root
 * CLAUDE.md's own Penjualan entry for the exact, now-conditional accrual wording.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fmtQty, fmtDateShort, today } from "../utils/idGenerators";
import { bucketLabel } from "../utils/transformations";
import { txnTotal, txnSisa, txnStatus, dueState } from "../utils/txnStatus";
import { useAvgCosts } from "../utils/wac";
import { useExpenses, LinkedExpensesDeleteNote } from "../components/ExpensesContext";
import { resolveBranchName, resolveLocationLabel } from "../utils/branches";
import { resolveSalespersonName, resolveSalespersonCode } from "../utils/salespeople";
import { displayContact } from "../utils/contacts";
import { transferKlienName } from "../utils/piutangUtang";
import TransactionFormModal from "../components/TransactionFormModal";
import TransactionDetailModal from "../components/TransactionDetailModal";
import BayarTagihanModal from "../components/BayarTagihanModal";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import KelolaSalesModal from "../components/KelolaSalesModal";
import InvoicePusat from "../components/print/InvoicePusat";
import SuratJalanDialog from "../components/print/SuratJalanDialog";
import SuratJalanPusat from "../components/print/SuratJalanPusat";
import PerLineCollyDialog from "../components/print/PerLineCollyDialog";
import Checklist from "../components/print/Checklist";
import InvoiceCabang from "../components/print/InvoiceCabang";
import SuratJalanCabang from "../components/print/SuratJalanCabang";
import DayNav from "../components/DayNav";
import TxnRowActions from "../components/TxnRowActions";
import TxnNilaiCell from "../components/TxnNilaiCell";
import { SubtotalBarangCell, RincianTambahanCell, TotalSetelahBiayaCell } from "../components/TxnCostCells";
import TxnBarangStokCell from "../components/TxnBarangStokCell";
import { StatusPill, DueBadge } from "../components/TxnBadges";

// Single source of truth for the Stok sub-column's width (dashboard alignment fix) — fed to
// BOTH the outer <colgroup>'s own Stok <col> (below, governs the header <th>'s width) AND
// TxnBarangStokCell's `stokWidth` prop (governs the merged body cell's internal name/stok
// split) so the two can never independently drift the way an inner CSS class's own hardcoded
// width once did — see TxnBarangStokCell.js's own header comment for the full story.
const STOK_COL_WIDTH = 110;

export default function Penjualan({
  transactions = [], items = [], contacts = [], salespeople = [], persons = [], branches = [], siblingLinks = [],
  onCreateTransaction, onUpdateTransaction, onDeleteTransaction, onSavePacking,
  onSaveAddressOverride, onRecordPayment,
  onUpdateSalesperson, onDeleteSalesperson, onMoveSalesperson, onCreateSalespersonRole, onUpdatePerson, onDeletePerson,
  onCreateContact, onCreateContactFull, onOpenItem, company, defaultDueDays,
}) {
  const [selectedDate, setSelectedDate] = useState(() => today());
  const [q, setQ] = useState("");
  const [searchBy, setSearchBy] = useState("semua"); // semua | invoice | klien | lokasi | barang | sales
  const [locFilter, setLocFilter] = useState("SEMUA"); // SEMUA | PUSAT | CABANG
  const [branchFilter, setBranchFilter] = useState("SEMUA"); // SEMUA | a branch id (Cabang tab only)
  // Hide-internal filter (section F, batch 4) — default OFF (internal SHOWN): hiding money by
  // default would be its own silent decision. Mirrors KontakPage's own "Sembunyikan yang
  // nonaktif" checkbox pattern, not a `.seg` — this is a plain on/off visibility preference, not
  // a 3-way scope like locFilter/branchFilter.
  const [hideInternal, setHideInternal] = useState(false);
  const [formModal, setFormModal] = useState(null); // null | { mode, type, initial }
  // Both detail and Bayar Tagihan are held as an ID, not a snapshotted object, and re-derived
  // live from `transactions` on every render (below) — a Bayar Tagihan add or an Edit
  // Transaksi payment correction must be visible immediately without closing the modal, which
  // a stale captured object would never reflect. Mirrors the "derive from live data by id, not
  // a stored object" pattern App.js's own `selectedItem` already uses for DetailBarangModal.
  const [detailTxnId, setDetailTxnId] = useState(null);
  const [bayarTagihanTxnId, setBayarTagihanTxnId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [printTarget, setPrintTarget] = useState(null); // T2c-1: Invoice Pusat print view
  // T2c-2: Surat Jalan Pusat / Checklist each need a dialog (colly capture) BEFORE their own
  // print view — PengepakanModal (a single generic dialog) is retired; its fields now live in
  // these two document-specific dialogs.
  const [suratJalanDialogTarget, setSuratJalanDialogTarget] = useState(null);
  const [suratJalanPrintTarget, setSuratJalanPrintTarget] = useState(null);
  const [checklistDialogTarget, setChecklistDialogTarget] = useState(null);
  const [checklistPrintTarget, setChecklistPrintTarget] = useState(null);
  // T2d: Invoice Cabang / Surat Jalan Cabang mirror the same dialog-then-print-target shape —
  // SuratJalanDialog is the SAME component as the pusat flow above, reused unmodified, just
  // driven by its own state pair here.
  const [invoiceCabangDialogTarget, setInvoiceCabangDialogTarget] = useState(null);
  const [invoiceCabangPrintTarget, setInvoiceCabangPrintTarget] = useState(null);
  const [suratJalanCabangDialogTarget, setSuratJalanCabangDialogTarget] = useState(null);
  const [suratJalanCabangPrintTarget, setSuratJalanCabangPrintTarget] = useState(null);
  const [kelolaSalesOpen, setKelolaSalesOpen] = useState(false); // T1e: moved out of the form

  const byCode = useMemo(() => new Map(items.map((it) => [it.internalCode, it])), [items]);
  const contactsById = useMemo(() => new Map(contacts.map((c) => [c.id, c])), [contacts]);
  const salespeopleById = useMemo(() => new Map(salespeople.map((s) => [s.id, s])), [salespeople]);

  // Live-derived (see the state comment above) — a stale id (e.g. the transaction was deleted
  // out from under an open modal) naturally resolves to null.
  const detailTxn = detailTxnId ? transactions.find((t) => t.id === detailTxnId) || null : null;
  const bayarTagihanTxn = bayarTagihanTxnId ? transactions.find((t) => t.id === bayarTagihanTxnId) || null : null;

  // Derived on demand from ALL transactions — WAC is bucket-blind and never persisted (T1a).
  // Shared memoization wrapper (fix #5) — see utils/wac.js's own header comment.
  // A purchase's own costs (Tagihan/Potongan/linked Biaya Lain-Lain) go into HPP — utils/wac.js.
  const { expenses } = useExpenses();
  const avgCosts = useAvgCosts(transactions, expenses);

  // PENJUALAN + TRANSFER share this list (PRD §5.3 — no fourth page); PEMBELIAN has its own.
  const listTxns = useMemo(() => transactions.filter((t) => t.type === "PENJUALAN" || t.type === "TRANSFER"), [transactions]);
  // Location filter (PRD §7.1): a transaction's stored `location` is always its SOURCE bucket
  // for a TRANSFER (App.js's createTransaction stores draft.location verbatim, and the form
  // derives transferDirection FROM location, never the reverse) — filtering on `t.location`
  // directly is therefore already anchor-only-correct, no special-casing needed.
  const locScoped = useMemo(
    () => (locFilter === "SEMUA" ? listTxns : listTxns.filter((t) => t.location === locFilter)),
    [listTxns, locFilter]
  );
  // Second-level branch filter (multi-branch cabang feature) — meaningful only while
  // locFilter === "CABANG" (see the visibility rule below); the `locFilter !== "CABANG" ||`
  // guard is belt-and-suspenders on top of the reset effect below (which already forces
  // branchFilter back to "SEMUA" on tab-leave) so this filter can never silently apply on a
  // tab where it shouldn't, even for one stray render. Splices in AFTER the Pusat/Cabang split
  // so the day cards (which reduce over dayTxns) respect it for free, same "for free"
  // precedent locFilter itself established.
  const branchScoped = useMemo(
    () => (locFilter !== "CABANG" || branchFilter === "SEMUA")
      ? locScoped
      : locScoped.filter((t) => t.branchId === branchFilter),
    [locScoped, locFilter, branchFilter]
  );
  // Hide-internal filter (section F, batch 4) — spliced BEFORE dayTxns, same "day cards respect
  // it for free" precedent branchScoped above already established: ticking this is the user
  // saying "show me only real business," so the accrual day cards below must follow, not just
  // the rendered table — two numbers (a day card above, a table below) disagreeing about what
  // happened today is exactly the failure this page's own work has been closing. The root
  // CLAUDE.md's "Total Nilai Penjualan ... including transfers, regardless of payment status"
  // rule stays true for the default (unchecked) state; ticking the box changes what's being
  // looked at, so the cards change with it.
  const internalScoped = useMemo(
    () => (hideInternal ? branchScoped.filter((t) => t.type !== "TRANSFER") : branchScoped),
    [branchScoped, hideInternal]
  );
  const dayTxns = useMemo(() => internalScoped.filter((t) => t.date === selectedDate), [internalScoped, selectedDate]);

  // Leaving the Cabang tab resets both new filters — without this, a previously-picked branch
  // filter would silently reapply the next time the user returns to Cabang, and
  // searchBy === "sales" while its own <option> is no longer rendered would leave the search
  // box silently matching nothing.
  useEffect(() => {
    if (locFilter !== "CABANG") {
      setBranchFilter("SEMUA");
      // "Sales" search-scope stays available on every tab now (invariant 12, revised — a
      // Pusat-anchored transaction carries a salesperson too), so it's no longer reset here.
    }
  }, [locFilter]);

  // Contact model redesign leak fix: a TRANSFER's own `contactId` always resolves to an
  // app-maintained internal stand-in whose `.name` is a fixed, never-real placeholder
  // (`"__internal__"`) — reading it directly would show that literal string on screen. Mirrors
  // Piutang & Utang's own `transferKlienName` fix for the identical column; a non-transfer
  // contact is never internal, so `displayContact` alone is correct there. Returns "" (never
  // the raw field) when nothing can be resolved.
  // useCallback (not a plain function) so its identity only changes when its real inputs do —
  // `visible` below can then list `klienDisplay` itself as a dependency instead of reaching past
  // it to `contactsById`/`branches` directly, which is what the exhaustive-deps rule actually
  // wants. This is not a behavior change: `visible` already recomputes exactly when `contactsById`
  // or `branches` change (it did so directly before); now it does so via `klienDisplay`'s own
  // reference changing for the same reason, computing the identical output either way.
  const klienDisplay = useCallback((t) => {
    if (t.type === "TRANSFER") return transferKlienName(t, branches) || "";
    const c = contactsById.get(t.contactId);
    return c ? displayContact(c) : "";
  }, [branches, contactsById]);
  // Search gap fix: the Lokasi badge's own display formula, named so the "Lokasi" search option
  // and the table cell can never disagree — same expression the JSX below renders, unconditional
  // on type (a TRANSFER's own `location`/`branchId` already ARE the source, Penjualan's own
  // perspective, so no flip is needed here the way Pembelian.js's own `lokasiDisplay` needs one).
  const lokasiDisplay = useCallback((t) => resolveLocationLabel(t.location, t.branchId, branches), [branches]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = dayTxns;
    if (needle) {
      list = list.filter((t) => {
        const invoiceHit = (t.invoiceNumber || "").toLowerCase().includes(needle);
        const klienHit = klienDisplay(t).toLowerCase().includes(needle);
        const lokasiHit = lokasiDisplay(t).toLowerCase().includes(needle);
        const barangHit = (t.lines || []).some((l) =>
          (l.name || "").toLowerCase().includes(needle) || (l.internalCode || "").toLowerCase().includes(needle));
        const sp = salespeopleById.get(t.salespersonId);
        const salesHit = (resolveSalespersonName(persons, sp) || "").toLowerCase().includes(needle) || (resolveSalespersonCode(persons, sp) || "").toLowerCase().includes(needle);
        if (searchBy === "invoice") return invoiceHit;
        if (searchBy === "klien") return klienHit;
        if (searchBy === "lokasi") return lokasiHit;
        if (searchBy === "barang") return barangHit;
        if (searchBy === "sales") return salesHit;
        return invoiceHit || klienHit || lokasiHit || barangHit || salesHit;
      });
    }
    // Sort by Waktu (chronological within the day — a ledger reads morning to evening).
    return [...list].sort((a, b) => {
      if (a.time !== b.time) return a.time < b.time ? -1 : 1;
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  }, [dayTxns, q, searchBy, klienDisplay, lokasiDisplay, salespeopleById, persons]);

  // ── Day cards (accrual — includes TRANSFER, "a sale by the source branch") ─────────────
  const totalNilai = dayTxns.reduce((s, t) => s + txnTotal(t), 0);
  const piutangHariIni = dayTxns.reduce((s, t) => s + txnSisa(t), 0);

  // ── Aksi handlers ────────────────────────────────────────────────────────────
  const openCreate = () => setFormModal({ mode: "create", type: "PENJUALAN", initial: null });
  const openTransfer = () => setFormModal({ mode: "create", type: "TRANSFER", initial: null });
  const openEdit = (t) => setFormModal({ mode: "edit", type: t.type, initial: t });

  const submitForm = (draft) => {
    if (formModal?.mode === "edit") onUpdateTransaction(formModal.initial.id, draft);
    else onCreateTransaction(draft);
    setFormModal(null);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Penjualan</h1>
          <div className="page-sub">Transaksi penjualan &amp; transfer per tanggal transaksi.</div>
        </div>
        <div style={{ display: "flex", gap: "var(--gap-md)" }}>
          {/* Invariant 12 (revised): salespeople are no longer a Cabang-only concept — a Pusat
              transaction requires one too — so this button is reachable from every tab now,
              not just Cabang. "Kelola Cabang" moved to Pengaturan (single home for app-level
              settings) — it no longer lives on this page at all. */}
          <button className="btn" onClick={() => setKelolaSalesOpen(true)}>Kelola Sales</button>
          <button className="btn" onClick={openTransfer}>Transfer Pusat↔Cabang</button>
          <button className="btn btn-primary" onClick={openCreate}>+ Tambah Transaksi</button>
        </div>
      </div>

      <DayNav date={selectedDate} onChange={setSelectedDate} />

      {/* Post-launch fix: the branch toggle moved onto the SAME row as the primary toggle,
          pushed to the far right — a deliberate deviation from the original stacked-rows
          design, per Chalton's own review. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "var(--gap-lg)", marginBottom: "var(--gap-lg)", flexWrap: "wrap" }}>
        <div className="seg">
          <button type="button" className={locFilter === "SEMUA" ? "active" : ""} onClick={() => setLocFilter("SEMUA")}>Semua</button>
          <button type="button" className={locFilter === "PUSAT" ? "active" : ""} onClick={() => setLocFilter("PUSAT")}>Pusat</button>
          <button type="button" className={locFilter === "CABANG" ? "active" : ""} onClick={() => setLocFilter("CABANG")}>Cabang</button>
        </div>

        {locFilter === "CABANG" && (
          <div className="seg">
            <button type="button" className={branchFilter === "SEMUA" ? "active" : ""} onClick={() => setBranchFilter("SEMUA")}>Semua Cabang</button>
            {branches.map((b) => (
              <button key={b.id} type="button" className={branchFilter === b.id ? "active" : ""} onClick={() => setBranchFilter(b.id)}>{b.name}</button>
            ))}
          </div>
        )}
      </div>

      <div className="daycards">
        <div className="daycard">
          <div className="daycard__label">Transaksi</div>
          <div className="daycard__value">{dayTxns.length}</div>
        </div>
        <div className="daycard">
          <div className="daycard__label">Total Nilai Penjualan</div>
          <div className="daycard__value">{fmtQty(totalNilai)}</div>
        </div>
        <div className="daycard daycard--piutang">
          <div className="daycard__label">Piutang Hari Ini</div>
          <div className="daycard__value">{fmtQty(piutangHariIni)}</div>
        </div>
      </div>

      <div className="toolbar">
        <select className="form-select search-by" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
          <option value="semua">Semua</option>
          <option value="invoice">No. Invoice</option>
          <option value="klien">Pembeli</option>
          <option value="lokasi">Lokasi</option>
          <option value="barang">Barang</option>
          <option value="sales">Sales</option>
        </select>
        <input className="form-input grow" placeholder="Cari transaksi hari ini…"
          value={q} onChange={(e) => setQ(e.target.value)} />
        {/* Hide-internal filter (section F, batch 4) — same checkbox shape as KontakPage's own
            "Sembunyikan yang nonaktif". Default OFF/unchecked, matching the day cards below. */}
        <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)", whiteSpace: "nowrap", fontSize: 13 }}>
          <input type="checkbox" checked={hideInternal}
            onChange={(e) => setHideInternal(e.target.checked)} />
          Sembunyikan transfer internal
        </label>
      </div>

      {dayTxns.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
          Belum ada transaksi penjualan pada tanggal ini.
        </div>
      ) : visible.length === 0 ? (
        <div className="empty-state">Tidak ada transaksi yang cocok dengan pencarian.</div>
      ) : (
        <div className="table-wrap">
          <table className="table txn-table txn-table--fixed">
            <colgroup>
              <col style={{ width: 95 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 70 }} />
              {/* Barang — dashboard alignment fix, round 3: recalibrated 230px -> 215px against
                  the real catalog's own p90-p95 name-length range (25-27 chars), not the rare
                  35-char max round 1 was aimed at (which real measurement showed 230px never
                  actually fit unwrapped anyway — 26 real chars measures 186px ink width in this
                  table's real Segoe UI 14px/400; +24px of .barangstok-inner__name's own padding
                  + a small real rendering margin lands on 215px). See TxnBarangStokCell.js's
                  own header comment for the full real-measured numbers. */}
              <col style={{ width: 215 }} />
              <col style={{ width: STOK_COL_WIDTH }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 140 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Tanggal &amp; Waktu</th>
                <th>No. Invoice</th>
                <th className="klien-cell">Pembeli</th>
                <th className="lokasi-cell">Lokasi</th>
                <th className="sales-cell">Sales</th>
                <th>Barang</th>
                <th className="num">Stok</th>
                <th className="num">Subtotal Barang</th>
                <th className="num">Tagihan &amp; Potongan</th>
                <th className="num">Total Nilai</th>
                <th className="num">Total Setelah Biaya</th>
                <th className="status-cell">Status</th>
                <th className="due-cell">Jatuh Tempo</th>
                <th className="aksi-cell">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => {
                const isTransfer = t.type === "TRANSFER";
                const salesperson = salespeopleById.get(t.salespersonId);
                // Piutang & Utang batch 3 — perspective-threaded (was a bare `txnStatus(t)`,
                // which hardcoded a non-Lunas TRANSFER to PIUTANG regardless of page). This
                // page shows a Transfer AS A SALE BY THE SOURCE (location/branchId already ARE
                // the source — Penjualan's own perspective), so the real per-side answer is
                // provably identical to the old hardcode: always PIUTANG. No visible change —
                // this just stops relying on the hardcode now that a real mechanism exists, so
                // nothing on this page still depends on txnStatus()'s own perspective-less
                // fallback. See Pembelian.js's own identical comment for the mirror-image case
                // that DOES visibly change (closing the documented StatusPill inconsistency).
                const status = txnStatus(t, t.location === "PUSAT" ? null : t.branchId);
                const due = dueState(t, today());
                const lines = t.lines || [];
                const destBucket = t.location === "PUSAT" ? "CABANG" : "PUSAT";
                return (
                  <tr key={t.id} onClick={() => setDetailTxnId(t.id)} className={isTransfer ? "txn-row--transfer" : undefined}>
                    <td className="date-cell">{fmtDateShort(t.date)}<br /><span style={{ color: "var(--muted)", fontSize: 12 }}>{t.time}</span></td>
                    <td className="mono" style={{ color: "var(--blue)" }}>
                      {t.invoiceNumber || "—"}
                      {(t.costItems || []).length > 0 && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>+Rincian</span>}
                    </td>
                    <td className="klien-cell">{klienDisplay(t) || "—"}</td>
                    <td className="lokasi-cell">
                      <span className="chip chip--muted">{lokasiDisplay(t)}</span>
                      {isTransfer && <span className="chip chip--transfer" style={{ marginLeft: "var(--gap-sm)" }}>TRANSFER</span>}
                    </td>
                    <td className="sales-cell">{salesperson ? (resolveSalespersonCode(persons, salesperson) || "—") : "—"}</td>
                    <TxnBarangStokCell
                      lines={lines}
                      byCode={byCode}
                      stokWidth={STOK_COL_WIDTH}
                      renderStok={(l) =>
                        isTransfer ? (
                          <>
                            <span style={{ color: "var(--red)" }}>−{fmtQty(l.qty)} PCS {bucketLabel(t.location)}</span>
                            {" → "}
                            <span style={{ color: "var(--green)" }}>+{fmtQty(l.qty)} PCS {bucketLabel(destBucket)}</span>
                          </>
                        ) : (
                          <span style={{ color: "var(--red)" }}>−{fmtQty(l.qty)} PCS</span>
                        )
                      }
                    />
                    <SubtotalBarangCell txn={t} />
                    <RincianTambahanCell txn={t} />
                    <TxnNilaiCell txn={t} />
                    <TotalSetelahBiayaCell txn={t} perspective="PENJUALAN" />
                    <td className="status-cell"><StatusPill status={status} /></td>
                    <td className="due-cell">{due.kind === "none" ? "—" : <DueBadge due={due} />}</td>
                    <td className="row-actions aksi-cell" onClick={(e) => e.stopPropagation()}>
                      <TxnRowActions
                        onEdit={() => openEdit(t)}
                        onBayarTagihan={status !== "LUNAS" ? () => setBayarTagihanTxnId(t.id) : undefined}
                        txnType={t.type}
                        onPrintInvoicePusat={t.location === "PUSAT" ? () => setPrintTarget(t) : undefined}
                        onPrintSuratJalanPusat={t.location === "PUSAT" ? () => setSuratJalanDialogTarget(t) : undefined}
                        onPrintChecklist={t.location === "PUSAT" ? () => setChecklistDialogTarget(t) : undefined}
                        onPrintInvoiceCabang={t.location === "CABANG" ? () => setInvoiceCabangDialogTarget(t) : undefined}
                        onPrintSuratJalanCabang={t.location === "CABANG" ? () => setSuratJalanCabangDialogTarget(t) : undefined}
                        onDelete={() => setDeleteTarget(t)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {formModal && (
        <TransactionFormModal
          mode={formModal.mode}
          type={formModal.type}
          initial={formModal.initial}
          items={items}
          contacts={contacts}
          salespeople={salespeople}
          persons={persons}
          branches={branches}
          siblingLinks={siblingLinks}
          avgCosts={avgCosts}
          defaultDueDays={defaultDueDays}
          onCreateContact={onCreateContact}
          onCreateContactFull={onCreateContactFull}
          onCreateSalespersonRole={onCreateSalespersonRole}
          onSubmit={submitForm}
          onClose={() => setFormModal(null)}
        />
      )}

      {kelolaSalesOpen && (
        <KelolaSalesModal salespeople={salespeople} transactions={transactions}
          branches={branches} persons={persons} branchFilter={branchFilter}
          onCreateSalespersonRole={onCreateSalespersonRole} onUpdatePerson={onUpdatePerson}
          onDeletePerson={onDeletePerson}
          onUpdate={onUpdateSalesperson} onMove={onMoveSalesperson} onDelete={onDeleteSalesperson}
          onClose={() => setKelolaSalesOpen(false)} />
      )}

      {detailTxn && (
        <TransactionDetailModal
          txn={detailTxn}
          items={items}
          contacts={contacts}
          salespeople={salespeople}
          persons={persons}
          branches={branches}
          today={today()}
          perspective="PENJUALAN"
          onClose={() => setDetailTxnId(null)}
        />
      )}

      {bayarTagihanTxn && (
        <BayarTagihanModal
          txn={bayarTagihanTxn}
          contacts={contacts}
          branches={branches}
          perspective="PENJUALAN"
          onRecordPayment={onRecordPayment}
          onClose={() => setBayarTagihanTxnId(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title="Hapus transaksi?"
          message={<>
            Anda akan menghapus transaksi <b className="mono">{deleteTarget.invoiceNumber || deleteTarget.id}</b>.
            Stok yang sudah dipotong akan dikembalikan.<LinkedExpensesDeleteNote txnId={deleteTarget.id} /> Tindakan ini permanen.
          </>}
          requireText="HAPUS"
          onConfirm={() => {
            onDeleteTransaction(deleteTarget.id);
            if (detailTxnId === deleteTarget.id) setDetailTxnId(null);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {printTarget && (
        <InvoicePusat
          txn={printTarget}
          items={items}
          company={company}
          onSaveAddressOverride={onSaveAddressOverride}
          onClose={() => setPrintTarget(null)}
        />
      )}

      {suratJalanDialogTarget && (
        <SuratJalanDialog
          txn={suratJalanDialogTarget}
          onSubmit={(payload) => {
            onSavePacking(suratJalanDialogTarget.id, payload);
            setSuratJalanPrintTarget({ ...suratJalanDialogTarget, packing: payload.packing });
            setSuratJalanDialogTarget(null);
          }}
          onClose={() => setSuratJalanDialogTarget(null)}
        />
      )}
      {suratJalanPrintTarget && (
        <SuratJalanPusat
          txn={suratJalanPrintTarget}
          onSaveAddressOverride={onSaveAddressOverride}
          onClose={() => setSuratJalanPrintTarget(null)}
        />
      )}

      {checklistDialogTarget && (
        <PerLineCollyDialog
          txn={checklistDialogTarget}
          items={items}
          title="Data Checklist"
          onSubmit={(payload) => {
            onSavePacking(checklistDialogTarget.id, payload);
            setChecklistPrintTarget({ ...checklistDialogTarget, lines: payload.lines });
            setChecklistDialogTarget(null);
          }}
          onClose={() => setChecklistDialogTarget(null)}
        />
      )}
      {checklistPrintTarget && (
        <Checklist
          txn={checklistPrintTarget}
          items={items}
          contact={contactsById.get(checklistPrintTarget.contactId)}
          branches={branches}
          company={company}
          onClose={() => setChecklistPrintTarget(null)}
        />
      )}

      {invoiceCabangDialogTarget && (
        <PerLineCollyDialog
          txn={invoiceCabangDialogTarget}
          items={items}
          title="Data Invoice Cabang"
          onSubmit={(payload) => {
            onSavePacking(invoiceCabangDialogTarget.id, payload);
            setInvoiceCabangPrintTarget({ ...invoiceCabangDialogTarget, lines: payload.lines });
            setInvoiceCabangDialogTarget(null);
          }}
          onClose={() => setInvoiceCabangDialogTarget(null)}
        />
      )}
      {invoiceCabangPrintTarget && (
        <InvoiceCabang
          txn={invoiceCabangPrintTarget}
          items={items}
          cabangName={resolveBranchName(branches, invoiceCabangPrintTarget.branchId) || undefined}
          onSaveAddressOverride={onSaveAddressOverride}
          onClose={() => setInvoiceCabangPrintTarget(null)}
        />
      )}

      {suratJalanCabangDialogTarget && (
        <SuratJalanDialog
          txn={suratJalanCabangDialogTarget}
          onSubmit={(payload) => {
            onSavePacking(suratJalanCabangDialogTarget.id, payload);
            setSuratJalanCabangPrintTarget({ ...suratJalanCabangDialogTarget, packing: payload.packing });
            setSuratJalanCabangDialogTarget(null);
          }}
          onClose={() => setSuratJalanCabangDialogTarget(null)}
        />
      )}
      {suratJalanCabangPrintTarget && (
        <SuratJalanCabang
          txn={suratJalanCabangPrintTarget}
          onSaveAddressOverride={onSaveAddressOverride}
          onClose={() => setSuratJalanCabangPrintTarget(null)}
        />
      )}
    </div>
  );
}
